import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import { ArAging, UnsentDrafts } from "api/Dashboard";
import { Panel, EmptyState, currency, compactCurrency } from "./shared";

/**
 * Money owed, by age - the figure nothing in the app surfaces today.
 *
 * AR AND UNSENT DRAFTS ARE SHOWN SEPARATELY AND ARE NEVER SUMMED.
 * An invoice created and never sent is not receivable: nobody has been
 * asked to pay it. Folding the two together is how the outstanding
 * figure came to read roughly five times its real size. The drafts line
 * sits underneath as its own statement, with its own words.
 * See KNOWN_ISSUES item 27.
 */
const ArAgingPanel: React.FC<{ ar: ArAging; unsentDrafts: UnsentDrafts }> = ({
  ar,
  unsentDrafts,
}) => {
  const navigate = useNavigate();
  const maxBucket = Math.max(1, ...ar.buckets.map((b) => b.amount));

  /**
   * `daysPastDue` is negative for an invoice that is not due yet - it
   * is DATEDIFF(now, dueDate). Printing it raw produced "-21 days past
   * due" on invoices that are current, in red, under a panel whose
   * whole job is telling late money from money that is fine.
   */
  const days = (n: number) => n + (n === 1 ? " day" : " days");
  const dueText = (dpd: number) =>
    dpd > 0 ? days(dpd) + " past due" : dpd === 0 ? "due today" : "due in " + days(-dpd);

  return (
    <Panel
      title="Money owed, by age"
      subtitle={
        ar.count === 0
          ? "Nothing outstanding"
          : ar.count +
            (ar.count === 1 ? " open invoice, " : " open invoices, ") +
            currency(ar.total) +
            " total"
      }
      sx={{ flex: "1 1 380px", minWidth: "320px" }}
    >
      {ar.count === 0 ? (
        <EmptyState
          headline="Nothing is owed right now"
          detail="Every invoice that has been sent is settled. This fills itself as new invoices go out."
        />
      ) : (
        <Box>
          {ar.buckets.map((bucket) => (
            <Box key={bucket.key} sx={{ marginBottom: "10px" }}>
              <Flex
                sx={{
                  justifyContent: "space-between",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                <Box sx={{ color: themevals.colors.dkgrey }}>
                  {bucket.label}
                  {bucket.count > 0 ? " (" + bucket.count + ")" : ""}
                </Box>
                <Box sx={{ fontWeight: 600, color: "base_800" }}>
                  {currency(bucket.amount)}
                </Box>
              </Flex>
              <Box
                sx={{
                  height: "6px",
                  borderRadius: "3px",
                  backgroundColor: "#F1F1F1",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: Math.round((bucket.amount / maxBucket) * 100) + "%",
                    backgroundColor:
                      bucket.key === "d90_plus"
                        ? themevals.colors.danger
                        : bucket.key === "current"
                          ? themevals.colors.success
                          : themevals.colors.warning,
                  }}
                />
              </Box>
            </Box>
          ))}

          {ar.oldest.length > 0 && (
            <Box sx={{ marginTop: "16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: themevals.colors.dkgrey,
                  marginBottom: "8px",
                }}
              >
                Oldest first
              </Box>
              {ar.oldest.slice(0, 5).map((row) => (
                <Flex
                  key={row.invoiceId}
                  onClick={() => navigate("/invoices/" + row.invoiceId)}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 0",
                    cursor: "pointer",
                    borderTop: "1px solid",
                    borderColor: themevals.colors.inputOutline,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                  }}
                >
                  <Box>
                    <Box sx={{ fontWeight: 600, color: "base_800" }}>
                      GMI-{row.sequence}
                    </Box>
                    <Box sx={{ color: themevals.colors.dkgrey }}>
                      {(row.clientName || "Unknown client") +
                        ", " +
                        dueText(row.daysPastDue)}
                    </Box>
                  </Box>
                  {/* Red only when it is actually late. */}
                  <Box
                    sx={{
                      fontWeight: 600,
                      color: row.daysPastDue > 0 ? themevals.colors.danger : "base_800",
                    }}
                  >
                    {compactCurrency(row.balance)}
                  </Box>
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      )}

      {unsentDrafts.count > 0 && (
        <Box
          sx={{
            marginTop: "16px",
            padding: "12px",
            borderRadius: "6px",
            backgroundColor: "#FEF3E0",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          <Box sx={{ fontSize: "12px", fontWeight: 600, color: "#D97706" }}>
            {unsentDrafts.count +
              (unsentDrafts.count === 1 ? " invoice worth " : " invoices worth ") +
              currency(unsentDrafts.value) +
              (unsentDrafts.count === 1 ? " has never been sent" : " have never been sent")}
          </Box>
          <Box
            sx={{
              fontSize: "12px",
              color: themevals.colors.dkgrey,
              marginTop: "4px",
              lineHeight: 1.5,
            }}
          >
            Not counted above. Nobody has been asked to pay these, so they are
            work to bill rather than money owed.
          </Box>
        </Box>
      )}
    </Panel>
  );
};

export default ArAgingPanel;
